import { usePerms } from 'contexts/PermsContext';
import { useEffect, useRef, useState } from 'react';
import { getUserProfileInfo, updateUserProfile } from 'services/user/user.service';
import { getAccountProfile, getAccountProfileUpdate } from '../utils/accountProfile.utils';

/** Load, edit, and save one account profile for either presentation. */
const useAccountProfile = () => {
  const permissions = usePerms();
  const canAccessProfile = Boolean(
    permissions?.testPermission?.([8, 9, 10, 11, 12, 13], ['ViewUsers']),
  );
  const [profile, setProfile] = useState(getAccountProfile);
  const [savedProfile, setSavedProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const activeProfileRequest = useRef(null);
  const saveInProgress = useRef(false);

  useEffect(() => {
    const request = {};
    activeProfileRequest.current = request;
    saveInProgress.current = false;
    setIsSaving(false);
    setMessage(null);
    setSavedProfile(null);
    setProfile(getAccountProfile());
    setError(null);
    setIsLoading(canAccessProfile);

    if (!canAccessProfile) {
      setError('You do not have permission to view this information.');
    } else {
      const loadProfile = async () => {
        try {
          const response = await getUserProfileInfo();
          const data = response?.data?.result;
          if (!data || typeof data !== 'object' || Array.isArray(data)) {
            throw new Error('Missing account profile');
          }
          if (activeProfileRequest.current !== request) return;

          const loadedProfile = getAccountProfile(data);
          setProfile(loadedProfile);
          setSavedProfile(loadedProfile);
        } catch {
          if (activeProfileRequest.current === request) {
            setError('Failed to load account information. Please try again.');
          }
        } finally {
          if (activeProfileRequest.current === request) setIsLoading(false);
        }
      };
      loadProfile();
    }

    return () => {
      activeProfileRequest.current = null;
    };
  }, [canAccessProfile]);

  const canEdit = canAccessProfile && !isLoading && savedProfile !== null;
  const hasChanges =
    savedProfile !== null && JSON.stringify(profile) !== JSON.stringify(savedProfile);
  const canSave = canEdit && hasChanges && !isSaving;

  const changeProfile = (change) => {
    if (!canEdit) return;
    setProfile((current) =>
      typeof change === 'function' ? change(current) : { ...current, ...change },
    );
    setError(null);
    setMessage(null);
  };

  const saveProfile = async () => {
    if (!canSave || saveInProgress.current) return;
    const request = activeProfileRequest.current;
    const submittedProfile = profile;
    const payload = getAccountProfileUpdate(submittedProfile);
    saveInProgress.current = true;
    setIsSaving(true);
    setError(null);
    setMessage(null);

    try {
      const response = await updateUserProfile(
        payload.phoneNumber,
        payload.gender,
        payload.birthday,
        payload.address,
        payload.emergencyContact,
      );
      if (activeProfileRequest.current !== request) return;
      if (response?.status !== 200) {
        throw new Error(response?.response?.data?.message || 'Unknown error occurred while saving');
      }

      // Edits made while saving remain unsaved; only this submitted draft becomes the baseline.
      setSavedProfile(submittedProfile);
      setMessage('Account information updated successfully');
    } catch (saveError) {
      if (activeProfileRequest.current === request) {
        const detail = saveError?.response?.data?.message || saveError?.message || 'Unknown error';
        setError(`Error updating user info: ${detail}`);
      }
    } finally {
      if (activeProfileRequest.current === request) {
        saveInProgress.current = false;
        setIsSaving(false);
      }
    }
  };

  return {
    profile,
    canEdit,
    canSave,
    isLoading,
    error,
    message,
    changeProfile,
    saveProfile,
  };
};

export default useAccountProfile;
